import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  offeringsExtractionApi,
  type OfferingProposal,
  type OfferingsProposal,
  ApiError,
} from '../api/client';

type Stage =
  | { kind: 'select-source' }
  | { kind: 'extracting' }
  | { kind: 'review'; proposal: OfferingsProposal; sourceType: 'URL' | 'PDF' | 'CSV'; sourceRef: string }
  | { kind: 'accepting' }
  | { kind: 'done'; count: number }
  | { kind: 'error'; message: string };

export function OfferingsExtractionPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [stage, setStage] = useState<Stage>({ kind: 'select-source' });

  if (!id) return null;

  if (stage.kind === 'select-source') {
    return (
      <SourceSelector
        tenantId={id}
        onResult={(r) =>
          setStage({ kind: 'review', proposal: r.proposal, sourceType: r.sourceType, sourceRef: r.sourceRef })
        }
        onError={(message) => setStage({ kind: 'error', message })}
        setStage={setStage}
      />
    );
  }
  if (stage.kind === 'extracting') {
    return <Layout><Spinner label="Asking Claude to extract the catalog…" /></Layout>;
  }
  if (stage.kind === 'review') {
    return (
      <ReviewForm
        tenantId={id}
        initial={stage.proposal}
        sourceType={stage.sourceType}
        sourceRef={stage.sourceRef}
        onAccepting={() => setStage({ kind: 'accepting' })}
        onAccepted={(count) => setStage({ kind: 'done', count })}
        onError={(message) => setStage({ kind: 'error', message })}
      />
    );
  }
  if (stage.kind === 'accepting') {
    return <Layout><Spinner label="Saving catalog proposal…" /></Layout>;
  }
  if (stage.kind === 'done') {
    return (
      <Layout>
        <div className="rounded-2xl bg-good/15 p-6 space-y-3">
          <p className="text-good font-medium">Catalog saved — {stage.count} offerings.</p>
          <p className="text-sm text-ink-muted">
            They'll be inserted into the tenant DB during provisioning (Phase 0.5).
          </p>
          <button
            onClick={() => navigate(`/tenants/${id}`)}
            className="text-sm text-ink underline-offset-4 hover:underline"
          >
            ← Back to tenant
          </button>
        </div>
      </Layout>
    );
  }
  return (
    <Layout>
      <div className="rounded-2xl bg-bad/15 p-6 space-y-3">
        <p className="text-bad font-medium">{stage.message}</p>
        <button
          onClick={() => setStage({ kind: 'select-source' })}
          className="text-sm text-ink underline-offset-4 hover:underline"
        >
          ← Try again
        </button>
      </div>
    </Layout>
  );
}

// ─── Source selector ─────────────────────────────────────────────────────

function SourceSelector({
  tenantId, onResult, onError, setStage,
}: {
  tenantId: string;
  onResult: (r: { proposal: OfferingsProposal; sourceType: 'URL' | 'PDF' | 'CSV'; sourceRef: string }) => void;
  onError: (message: string) => void;
  setStage: (s: Stage) => void;
}) {
  const [url, setUrl] = useState('');
  const [file, setFile] = useState<File | null>(null);

  async function submitUrl(e: FormEvent) {
    e.preventDefault();
    setStage({ kind: 'extracting' });
    try {
      const r = await offeringsExtractionApi.fromUrl(tenantId, url.trim());
      onResult({ proposal: r.proposal, sourceType: r.source_type, sourceRef: r.source_reference });
    } catch (err) {
      onError(err instanceof ApiError ? err.body.message ?? err.body.error ?? 'Extraction failed' : 'Extraction failed');
    }
  }

  async function submitFile(e: FormEvent) {
    e.preventDefault();
    if (!file) return;
    setStage({ kind: 'extracting' });
    try {
      const r = await offeringsExtractionApi.fromFile(tenantId, file);
      onResult({ proposal: r.proposal, sourceType: r.source_type, sourceRef: r.source_reference });
    } catch (err) {
      onError(err instanceof ApiError ? err.body.message ?? err.body.error ?? 'Extraction failed' : 'Extraction failed');
    }
  }

  return (
    <Layout>
      <p className="text-sm text-ink-muted">
        Provide a source — Aurem will use Claude to extract the clinic's services and products
        catalog. You'll review + edit before saving. Excel: export to CSV first.
      </p>

      <section className="rounded-2xl bg-surface-2 p-6 space-y-4">
        <h2 className="font-heading text-lg">From a website URL</h2>
        <form onSubmit={submitUrl} className="space-y-3">
          <input
            type="url"
            required
            placeholder="https://livemore.health/services"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            className="w-full rounded-lg bg-surface-3 px-3.5 py-2.5 text-ink outline-none ring-1 ring-transparent focus:ring-primary"
          />
          <button
            type="submit"
            disabled={!url}
            className="rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-ink transition disabled:opacity-50"
          >
            Extract from URL
          </button>
        </form>
      </section>

      <section className="rounded-2xl bg-surface-2 p-6 space-y-4">
        <h2 className="font-heading text-lg">From a PDF or CSV</h2>
        <form onSubmit={submitFile} className="space-y-3">
          <input
            type="file"
            accept="application/pdf,text/csv,.csv,.tsv,text/plain"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            className="block w-full text-sm text-ink-muted file:mr-3 file:rounded-md file:border-0 file:bg-surface-3 file:px-3 file:py-2 file:text-ink file:cursor-pointer"
          />
          <button
            type="submit"
            disabled={!file}
            className="rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-ink transition disabled:opacity-50"
          >
            Extract from file
          </button>
        </form>
      </section>
    </Layout>
  );
}

// ─── Review form ─────────────────────────────────────────────────────────

function ReviewForm({
  tenantId, initial, sourceType, sourceRef, onAccepting, onAccepted, onError,
}: {
  tenantId: string;
  initial: OfferingsProposal;
  sourceType: 'URL' | 'PDF' | 'CSV';
  sourceRef: string;
  onAccepting: () => void;
  onAccepted: (count: number) => void;
  onError: (message: string) => void;
}) {
  const [offerings, setOfferings] = useState<OfferingProposal[]>(initial.offerings);

  function updateOffering(idx: number, patch: Partial<OfferingProposal>) {
    setOfferings((curr) => curr.map((o, i) => (i === idx ? { ...o, ...patch } : o)));
  }

  function removeOffering(idx: number) {
    setOfferings((curr) => curr.filter((_, i) => i !== idx));
  }

  function addOffering() {
    setOfferings((curr) => [...curr, { type: 'SERVICE', name: '' }]);
  }

  async function onAccept(e: FormEvent) {
    e.preventDefault();
    onAccepting();
    try {
      const valid = offerings.filter((o) => o.name.trim().length > 0);
      const r = await offeringsExtractionApi.accept(tenantId, valid);
      onAccepted(r.count);
    } catch (err) {
      onError(err instanceof ApiError ? err.body.message ?? err.body.error ?? 'Save failed' : 'Save failed');
    }
  }

  return (
    <Layout>
      <p className="text-sm text-ink-muted">
        Source: <span className="font-mono text-ink">{sourceType}</span>{' '}
        — <span className="font-mono text-xs text-ink-muted">{sourceRef}</span>
      </p>

      {initial.decision_points && initial.decision_points.length > 0 && (
        <section className="rounded-2xl bg-warn/10 border border-warn/30 p-6 space-y-4">
          <h3 className="font-heading text-base text-warn">Claude flagged some questions</h3>
          {initial.decision_points.map((dp, i) => (
            <div key={i} className="space-y-1 text-sm">
              <p className="text-ink font-medium">{dp.question}</p>
              {dp.context && <p className="text-ink-muted">{dp.context}</p>}
              {dp.ai_recommendation && <p className="text-ink-muted">AI suggestion: {dp.ai_recommendation}</p>}
            </div>
          ))}
        </section>
      )}

      <form onSubmit={onAccept} className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-heading text-lg">{offerings.length} offerings</h2>
          <button
            type="button"
            onClick={addOffering}
            className="rounded-md bg-surface-3 px-3 py-1.5 text-sm text-ink-muted hover:text-ink"
          >
            + Add row
          </button>
        </div>

        <div className="space-y-3">
          {offerings.map((o, i) => (
            <div key={i} className="rounded-2xl bg-surface-2 p-4 space-y-3">
              <div className="flex items-start gap-2">
                <select
                  value={o.type}
                  onChange={(e) => updateOffering(i, { type: e.target.value as 'SERVICE' | 'PRODUCT' })}
                  className="rounded-md bg-surface-3 px-2 py-1.5 text-xs uppercase tracking-wider text-ink outline-none"
                >
                  <option value="SERVICE">Service</option>
                  <option value="PRODUCT">Product</option>
                </select>
                <input
                  type="text"
                  placeholder="Name"
                  value={o.name}
                  onChange={(e) => updateOffering(i, { name: e.target.value })}
                  className="flex-1 rounded-lg bg-surface-3 px-3 py-1.5 text-ink outline-none ring-1 ring-transparent focus:ring-primary"
                />
                <button
                  type="button"
                  onClick={() => removeOffering(i)}
                  aria-label="Remove offering"
                  className="text-ink-muted hover:text-bad text-xl leading-none px-2"
                >×</button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <input
                  type="text"
                  placeholder="Category (e.g. IV therapy)"
                  value={o.category ?? ''}
                  onChange={(e) => updateOffering(i, { category: e.target.value || undefined })}
                  className="rounded-md bg-surface-3 px-3 py-1.5 text-sm text-ink outline-none"
                />
                {o.type === 'SERVICE' ? (
                  <input
                    type="number"
                    placeholder="Duration (minutes)"
                    value={o.duration_minutes ?? ''}
                    onChange={(e) => updateOffering(i, { duration_minutes: e.target.value ? Number(e.target.value) : undefined })}
                    className="rounded-md bg-surface-3 px-3 py-1.5 text-sm text-ink outline-none"
                  />
                ) : (
                  <input
                    type="text"
                    placeholder="Unit (e.g. 60 capsules)"
                    value={o.unit ?? ''}
                    onChange={(e) => updateOffering(i, { unit: e.target.value || undefined })}
                    className="rounded-md bg-surface-3 px-3 py-1.5 text-sm text-ink outline-none"
                  />
                )}
              </div>
              <textarea
                placeholder="Description (patient-facing)"
                value={o.description ?? ''}
                onChange={(e) => updateOffering(i, { description: e.target.value || undefined })}
                rows={2}
                className="w-full rounded-md bg-surface-3 px-3 py-2 text-sm text-ink outline-none"
              />
            </div>
          ))}
        </div>

        <div className="flex gap-3 pt-4">
          <button
            type="submit"
            disabled={offerings.length === 0}
            className="rounded-lg bg-primary px-4 py-2.5 font-medium text-ink transition disabled:opacity-50"
          >
            Accept and save
          </button>
          <Link
            to={`/tenants/${tenantId}`}
            className="rounded-lg bg-surface-3 px-4 py-2.5 font-medium text-ink-muted hover:text-ink transition"
          >
            Cancel
          </Link>
        </div>
      </form>
    </Layout>
  );
}

function Layout({ children }: { children: React.ReactNode }) {
  const { id } = useParams();
  return (
    <div className="space-y-6">
      <Link to={`/tenants/${id}`} className="text-xs text-ink-muted hover:text-ink uppercase tracking-wider">
        ← Tenant
      </Link>
      <h1 className="font-heading text-2xl font-semibold tracking-tight">Clinic offerings extraction</h1>
      {children}
    </div>
  );
}

function Spinner({ label }: { label: string }) {
  return (
    <div className="rounded-2xl bg-surface-2 p-8 text-center space-y-3">
      <div className="inline-block animate-spin rounded-full h-6 w-6 border-2 border-ink-muted border-t-primary" />
      <p className="text-sm text-ink-muted">{label}</p>
    </div>
  );
}
